<template>
  <div class="page-outer">
    <div class="page-header page-header-line-cfg">
      <h1 class="page-title">{{ isEdit ? '編輯 LINE 設定' : '新增 LINE 設定' }}</h1>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="handleCancel">
          <i class="ri-close-line"></i>
          取消
        </button>
        <button
          v-if="currentStep > 0"
          type="button"
          class="btn btn-secondary"
          @click="previousStep"
        >
          <i class="ri-arrow-left-line"></i>
          上一步
        </button>
        <button
          v-if="currentStep < steps.length - 1"
          type="button"
          class="btn btn-primary"
          @click="nextStep"
          :disabled="!canProceedToNextStep"
        >
          下一步
          <i class="ri-arrow-right-line"></i>
        </button>
        <button
          v-if="isEdit && currentStep > 0 && currentStep < steps.length - 1"
          type="button"
          class="btn btn-success"
          :disabled="submitting || !canProceedToNextStep"
          @click="handleSubmit"
        >
          <span v-if="submitting"><i class="ri-loader-4-line spin"></i> 更新中...</span>
          <span v-else><i class="ri-save-line"></i> 更新設定</span>
        </button>
        <button
          v-if="currentStep === steps.length - 1"
          type="button"
          class="btn btn-primary"
          :disabled="submitting"
          @click="handleSubmit"
        >
          <span v-if="submitting">
            <i class="ri-loader-4-line spin"></i>
            {{ isEdit ? '更新中...' : '建立中...' }}
          </span>
          <span v-else>{{ isEdit ? '更新設定' : '建立設定' }}</span>
        </button>
        <button
          v-if="isEdit && currentStep > 0"
          type="button"
          class="btn btn-info"
          @click="handleTest"
          :disabled="testing"
        >
          <i class="ri-flashlight-line"></i>
          {{ testing ? '測試中...' : '測試連線' }}
        </button>
      </div>
    </div>

    <div class="page-container">
    <div class="form-container">
      <!-- 步驟導航 -->
      <div class="stepper">
        <div class="stepper-header">
          <div
            v-for="(step, index) in steps"
            :key="index"
            class="step-item"
            :class="{
              active: currentStep === index,
              completed: currentStep > index,
              disabled: currentStep < index,
              clickable: canNavigateToStep(index)
            }"
            @click="goToStep(index)"
          >
            <div class="step-number">
              <span v-if="currentStep > index" class="step-check">
                <i class="ri-check-line"></i>
              </span>
              <span v-else>{{ index + 1 }}</span>
            </div>
            <div class="step-label">{{ step.label }}</div>
          </div>
        </div>
        <div class="stepper-line">
          <div class="stepper-progress" :style="{ width: `${(currentStep / (steps.length - 1)) * 100}%` }"></div>
        </div>
      </div>

      <form @submit.prevent="handleSubmit">
        <!-- 步驟一：申請LINE官方帳號需知 -->
        <div v-show="currentStep === 0" class="step-content">
          <div class="form-section">
            <h2 class="section-title">步驟一：申請LINE官方帳號需知</h2>
            <p class="section-description">在開始設定之前，請先了解如何申請 LINE 官方帳號</p>

            <div class="info-box">
              <h3 class="info-title">
                <i class="ri-information-line"></i>
                重要資訊
              </h3>
              
              <div class="info-content">
                <h4>📋 申請步驟</h4>
                <ol class="info-list">
                  <li>
                    前往 <a href="https://entry.line.biz/form/entry/unverified" target="_blank" rel="noopener noreferrer">申請LINE官方帳號</a>
                  </li>
                  <li>
                    前往 <a href="https://developers.line.biz/en/" target="_blank" rel="noopener noreferrer">LINE Developers Console</a> 登入
                  </li>
                  <li>
                    建立 Provider
                  </li>
                  <li>
                    新增 Messaging API Channel<br>
                    取得 Channel ID、Channel Secret、Access Token、Bot Basic ID
                  </li>
                  <li>
                    新增 LINE Login Channel<br>
                    取得 Channel ID、Channel Secret
                  </li>
                </ol>

                <div class="danger-alert-box">
                  <h4>
                    <i class="ri-error-warning-fill"></i>
                    建議使用公司專用 LINE 帳號當管理者
                  </h4>
                  <p>為了避免人員異動造成的管理問題，建議使用公司專用的 LINE 帳號來申請和管理官方帳號，而不是使用個人帳號。</p>
                </div>

                <div class="collapsible-section">
                  <button 
                    type="button" 
                    class="collapsible-header" 
                    @click="toggleStepDetail('businessAccount')"
                  >
                    <h4>💼 什麼是 LINE 商用帳號（LINE Business Account）？</h4>
                    <i :class="expandedSteps.businessAccount ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
                  </button>
                  <div v-show="expandedSteps.businessAccount" class="explanation-box">
                  <p class="highlight-text">
                    <strong>「商用帳號」不是一個全新的 LINE 身分</strong>，而是「把你的 LINE 個人帳號拿來做商業用途管理」的一套後台與權限系統。
                  </p>

                  <table class="comparison-table">
                    <thead>
                      <tr>
                        <th>項目</th>
                        <th>一般個人 LINE 帳號</th>
                        <th>LINE 商用帳號（LINE Business）</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><strong>用途</strong></td>
                        <td>私人聊天、加好友</td>
                        <td>管理官方帳號、Bot、登入、廣告</td>
                      </tr>
                      <tr>
                        <td><strong>登入方式</strong></td>
                        <td>LINE App</td>
                        <td>仍然用你的 LINE 個人帳號登入</td>
                      </tr>
                      <tr>
                        <td><strong>是否要另外註冊</strong></td>
                        <td>❌ 不用</td>
                        <td>❌ 不用（只是授權角色）</td>
                      </tr>
                      <tr>
                        <td><strong>能做的事</strong></td>
                        <td>聊天、貼文、加群</td>
                        <td>建立 LINE 官方帳號、Messaging API、LINE Login、廣告帳戶</td>
                      </tr>
                      <tr>
                        <td><strong>權限管理</strong></td>
                        <td>無</td>
                        <td>✅ 可多位管理者、角色分工</td>
                      </tr>
                    </tbody>
                  </table>

                  <div class="key-points">
                    <p><strong>👉 LINE 商用帳號 ≠ LINE 官方帳號</strong></p>
                    <p><strong>👉 LINE 商用帳號 ≠ 新的 LINE 帳號</strong></p>
                  </div>

                  <h5>商用帳號的功能</h5>
                  <p>用你的 LINE 個人帳號登入 <a href="https://account.line.biz/login" target="_blank" rel="noopener noreferrer">LINE Business Account 後台</a> 後，可以：</p>
                  <ul class="info-list">
                    <li>管理 LINE 官方帳號（OA）</li>
                    <li>建立 / 管理 Messaging API Channel</li>
                    <li>設定 LINE Login</li>
                    <li>管理廣告帳號</li>
                    <li>邀請其他人一起管理</li>
                  </ul>

                  <div class="note-box">
                    <p><strong>📌 重要提醒：</strong>它不是聊天用的，也不會影響你原本的 LINE 好友或聊天紀錄</p>
                  </div>

                  <h5>常見誤解澄清</h5>
                  <ul class="info-list">
                    <li><strong>❌ 商用帳號會不會變成公司帳？</strong><br>→ <strong>不會</strong>，它只是賦予你「管理公司資源的權限」</li>
                    <li><strong>❌ 商用帳號會被客戶看到？</strong><br>→ <strong>不會</strong>，客戶只會看到「LINE 官方帳號」，看不到你本人</li>
                    <li><strong>❌ 需要另外辦一個 LINE？</strong><br>→ <strong>不需要</strong>，很多公司老闆都直接用自己 LINE 管理（但建議使用公司專用 LINE 帳號）</li>
                  </ul>

                  </div>
                </div>

                <div class="summary-box">
                  <p><strong>用一句話總結：</strong></p>
                  <p>LINE 商用帳號 = 用你的 LINE 個人帳號，登入 LINE 提供的「企業管理後台」，用來管理官方帳號、Bot、登入與廣告。</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 步驟二：基本設定 -->
        <div v-if="currentStep === 1" class="step-content">
          <div class="form-section">
            <h2 class="section-title">步驟二：基本設定</h2>
            <p class="section-description">設定 LINE 官方帳號的基本資訊</p>
            
            <!-- 選擇客戶（只有 system 和 platform 帳號在新增時需要） -->
            <div v-if="needsClientSelection && !isEdit" class="form-group">
              <label class="required">選擇客戶</label>
              <p class="section-description" style="margin-bottom: 12px;">請選擇要為哪個客戶建立 LINE 官方帳號設定</p>
              
              <div v-if="loadingClients" class="loading-message">
                <i class="ri-loader-4-line spin"></i>
                載入客戶列表中...
              </div>
              
              <div v-else>
                <div class="client-search-wrapper">
                  <input
                    type="text"
                    v-model="clientSearchKeyword"
                    @input="handleClientSearch"
                    class="form-control"
                    placeholder="輸入客戶代碼或名稱進行搜尋..."
                    :class="{ 'is-invalid': errors.clientId }"
                  />
                  <i class="ri-search-line search-icon"></i>
                </div>
                
                <!-- 搜尋結果列表 -->
                <div v-if="showClientList && filteredClients.length > 0" class="client-list">
                  <div
                    v-for="client in filteredClients"
                    :key="client.id"
                    class="client-item"
                    :class="{ 'selected': form.clientId === client.id }"
                    @click="selectClient(client)"
                  >
                    <div class="client-info">
                      <div class="client-name">{{ client.clientName }}</div>
                      <div class="client-code">{{ client.clientCode }}</div>
                    </div>
                    <i v-if="form.clientId === client.id" class="ri-check-line"></i>
                  </div>
                </div>
                
                <!-- 已選擇的客戶 -->
                <div v-if="selectedClient" class="selected-client">
                  <div class="selected-label">已選擇客戶：</div>
                  <div class="selected-info">
                    <div>
                      <strong>{{ selectedClient.clientName }}</strong>
                      <span class="client-code-tag">{{ selectedClient.clientCode }}</span>
                    </div>
                    <button
                      type="button"
                      class="btn-clear"
                      @click="clearClientSelection"
                      title="清除選擇"
                    >
                      <i class="ri-close-line"></i>
                    </button>
                  </div>
                </div>
                
                <!-- 沒有搜尋結果 -->
                <div v-if="clientSearchKeyword && showClientList && filteredClients.length === 0" class="no-results">
                  <i class="ri-search-line"></i>
                  找不到符合「{{ clientSearchKeyword }}」的客戶
                </div>
                
                <div v-if="errors.clientId" class="invalid-feedback">
                  {{ errors.clientId }}
                </div>
                
                <p class="form-hint">
                  <i class="ri-information-line"></i>
                  請輸入關鍵字搜尋並選擇客戶（載入前 100 筆啟用客戶）
                </p>
              </div>
            </div>
            
            <!-- 客戶資訊顯示（客戶帳號自動帶入或編輯模式顯示） -->
            <div v-if="currentClientInfo" class="form-group">
              <label>客戶資訊</label>
              <div class="selected-client">
                <div class="selected-info">
                  <div>
                    <strong>{{ currentClientInfo.clientName }}</strong>
                    <span class="client-code-tag">{{ currentClientInfo.clientCode }}</span>
                  </div>
                </div>
              </div>
              <p class="form-hint">
                <i class="ri-information-line"></i>
                {{ isEdit ? '此設定的客戶資訊（不可修改）' : '此設定將為當前客戶帳號建立' }}
              </p>
            </div>
            
            <div class="form-group">
              <label class="required">設定名稱</label>
              <input
                v-model="form.configName"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.configName }"
                placeholder="例如：主要 LINE 官方帳號"
                required
              />
              <div v-if="errors.configName" class="invalid-feedback">
                {{ errors.configName }}
              </div>
              <p class="form-hint form-hint-important">用於識別此 LINE 官方帳號設定，強烈建議與官方帳號同名</p>
            </div>

            <div class="form-group">
              <label>啟用狀態</label>
              <select v-model="form.isActive" class="form-control">
                <option :value="true">啟用</option>
                <option :value="false">停用</option>
              </select>
              <p class="form-hint">啟用後，此 LINE 官方帳號設定可用於推播訊息和接收 Webhook 事件</p>
            </div>
          </div>
        </div>

        <!-- 步驟三：Messaging API 設定 -->
        <div v-if="currentStep === 2" class="step-content">
          <div class="form-section">
            <h2 class="section-title">
              <i class="ri-message-3-line"></i>
              步驟三：Messaging API 設定
            </h2>
            <p class="section-description">用於訊息推播和接收 Webhook 事件</p>

            <!-- 第一組：Channel ID 和 Channel Secret -->
            <div class="form-group-section">
              <div class="section-note">
                <i class="ri-information-line"></i>
                <span>請在 LINE Developers Console &gt; Messaging API 中的 <strong>Basic settings</strong> 頁籤之下找到以下資訊</span>
              </div>
              
              <div class="form-group">
                <label class="required">Channel ID</label>
                <input
                  v-model="form.messagingChannelId"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': errors.messagingChannelId }"
                  placeholder="例如：1234567890"
                  required
                  @blur="validateMessagingChannelId"
                />
                <div v-if="errors.messagingChannelId" class="invalid-feedback">
                  {{ errors.messagingChannelId }}
                </div>
                <p class="form-hint">請輸入純數字（0-9）的 Channel ID</p>
              </div>

              <div class="form-group">
                <label class="required">Channel Secret</label>
                <div class="input-group">
                  <input
                    v-model="form.messagingChannelSecret"
                    :type="showMessagingChannelSecret ? 'text' : 'password'"
                    class="form-control"
                    :class="{ 'is-invalid': errors.messagingChannelSecret }"
                    :placeholder="isEdit ? '留空則不更新，輸入新值則更新' : '輸入 Channel Secret'"
                    :required="!isEdit"
                  />
                  <button
                    type="button"
                    class="btn btn-outline-secondary"
                    @click="showMessagingChannelSecret = !showMessagingChannelSecret"
                    :title="showMessagingChannelSecret ? '隱藏' : '顯示'"
                  >
                    <i :class="showMessagingChannelSecret ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
                  </button>
                </div>
                <div v-if="errors.messagingChannelSecret" class="invalid-feedback">
                  {{ errors.messagingChannelSecret }}
                </div>
                <p class="form-hint">用於驗證 Webhook 簽章，系統會自動加密存儲</p>
              </div>
            </div>

            <!-- 第二組：Bot Basic ID, Channel Access Token, Access Token 過期時間 -->
            <div class="form-group-section">
              <div class="section-note">
                <i class="ri-information-line"></i>
                <span>請在 LINE Developers Console &gt; Messaging API 中的 <strong>Messaging API</strong> 頁籤之下找到以下資訊</span>
              </div>
              
              <div class="form-group">
                <label class="required">Bot Basic ID</label>
                <input
                  v-model="form.messagingBotBasicId"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': errors.messagingBotBasicId }"
                  placeholder="例如：@abc123"
                  required
                  @blur="validateMessagingBotBasicId"
                />
                <div v-if="errors.messagingBotBasicId" class="invalid-feedback">
                  {{ errors.messagingBotBasicId }}
                </div>
                <p class="form-hint">必須以 @ 開頭，例如：@abc123</p>
              </div>

              <div class="form-group">
                <label class="required">Channel Access Token</label>
                <div class="input-group">
                  <input
                    v-model="form.messagingAccessToken"
                    :type="showMessagingAccessToken ? 'text' : 'password'"
                    class="form-control"
                    :class="{ 'is-invalid': errors.messagingAccessToken }"
                    :placeholder="isEdit ? '留空則不更新，輸入新值則更新' : '輸入 Channel Access Token'"
                    :required="!isEdit"
                  />
                  <button
                    type="button"
                    class="btn btn-outline-secondary"
                    @click="showMessagingAccessToken = !showMessagingAccessToken"
                    :title="showMessagingAccessToken ? '隱藏' : '顯示'"
                  >
                    <i :class="showMessagingAccessToken ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
                  </button>
                </div>
                <div v-if="errors.messagingAccessToken" class="invalid-feedback">
                  {{ errors.messagingAccessToken }}
                </div>
                <p class="form-hint">用於呼叫 Messaging API，系統會自動加密存儲</p>
              </div>

              <div class="form-group">
                <label>Access Token 過期時間</label>
                <input
                  v-model="form.messagingAccessTokenExpiresAt"
                  type="datetime-local"
                  class="form-control"
                  :class="{ 'is-invalid': errors.messagingAccessTokenExpiresAt }"
                />
                <div v-if="errors.messagingAccessTokenExpiresAt" class="invalid-feedback">
                  {{ errors.messagingAccessTokenExpiresAt }}
                </div>
                <p class="form-hint">選填，Token 過期時間（系統會自動更新）</p>
              </div>
            </div>

            <!-- Webhook URL 顯示（新增和編輯模式都顯示） -->
            <div v-if="form.messagingChannelId" class="form-group">
              <label>Webhook URL</label>
              <div class="input-group">
                <input
                  :value="generatedWebhookUrl"
                  type="text"
                  class="form-control"
                  readonly
                  @click="$event.target.select()"
                />
                <button
                  type="button"
                  class="btn btn-secondary"
                  @click="copyToClipboard(generatedWebhookUrl, 'Webhook URL')"
                >
                  <i class="ri-file-copy-line"></i>
                  複製
                </button>
              </div>
              <div class="webhook-url-alert">
                <div class="alert-icon">
                  <i class="ri-alert-fill"></i>
                </div>
                <div class="alert-content">
                  <div class="alert-title">
                    <strong>重要提醒</strong>
                  </div>
                  <div class="alert-message">
                    請將此 URL 複製，並貼回到 <strong>LINE Developers Console</strong> 後台的 Messaging API 中的 <strong>Messaging API</strong> 頁籤之下的 <strong>Webhook URL</strong> 欄位<br>
                    同時要將 <strong>Use webhook</strong> 的選項設為 <strong>開啟</strong> 的狀態
                  </div>
                  <div class="alert-guide-image">
                    <img src="/images/line-guide/use-webhook.png" alt="Use webhook 開啟示意圖" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 步驟四：LINE Login 設定 -->
        <div v-if="currentStep === 3" class="step-content">
          <div class="form-section">
            <h2 class="section-title">
              <i class="ri-login-box-line"></i>
              步驟四：LINE Login 設定
            </h2>
            <p class="section-description">用於會員登入和帳號綁定</p>

            <!-- 第一組：Channel ID 和 Channel Secret -->
            <div class="form-group-section">
              <div class="section-note">
                <i class="ri-information-line"></i>
                <span>請在 LINE Developers Console &gt; LINE Login 中的 <strong>Basic settings</strong> 頁籤中找到以下資訊</span>
              </div>
              
              <div class="form-group">
                <label class="required">Channel ID</label>
                <input
                  v-model="form.loginChannelId"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': errors.loginChannelId }"
                  placeholder="例如：1234567890"
                  required
                  @blur="validateLoginChannelId"
                />
                <div v-if="errors.loginChannelId" class="invalid-feedback">
                  {{ errors.loginChannelId }}
                </div>
                <p class="form-hint">LINE Login Channel ID（可與 Messaging API 相同或不同），請輸入純數字（0-9）</p>
              </div>

              <div class="form-group">
                <label :class="{ 'required': !isEdit }">Channel Secret</label>
                <div class="input-group">
                  <input
                    v-model="form.loginChannelSecret"
                    :type="showLoginChannelSecret ? 'text' : 'password'"
                    class="form-control"
                    :class="{ 'is-invalid': errors.loginChannelSecret }"
                    :placeholder="isEdit ? '留空則不更新，輸入新值則更新' : '輸入 Channel Secret'"
                    :required="!isEdit"
                  />
                  <button
                    type="button"
                    class="btn btn-outline-secondary"
                    @click="showLoginChannelSecret = !showLoginChannelSecret"
                    :title="showLoginChannelSecret ? '隱藏' : '顯示'"
                  >
                    <i :class="showLoginChannelSecret ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
                  </button>
                </div>
                <div v-if="errors.loginChannelSecret" class="invalid-feedback">
                  {{ errors.loginChannelSecret }}
                </div>
                <p class="form-hint">LINE Login Channel Secret，系統會自動加密存儲</p>
              </div>
            </div>

            <!-- 第二組：Callback URL -->
            <div class="form-group-section">
              <div class="form-group">
                <label>Callback URL</label>
                <div class="input-group">
                  <input
                    v-model="form.loginCallbackUrl"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.loginCallbackUrl }"
                    :placeholder="generatedCallbackUrl || 'https://...'"
                    @focus="onCallbackUrlFocus"
                  />
                  <button
                    type="button"
                    class="btn btn-secondary"
                    @click="useGeneratedCallbackUrl"
                    title="使用自動生成的 URL"
                  >
                    <i class="ri-refresh-line"></i>
                    自動生成
                  </button>
                  <button
                    type="button"
                    class="btn btn-secondary"
                    @click="copyToClipboard(form.loginCallbackUrl || generatedCallbackUrl, 'Callback URL')"
                  >
                    <i class="ri-file-copy-line"></i>
                    複製
                  </button>
                </div>
                <div v-if="errors.loginCallbackUrl" class="invalid-feedback">
                  {{ errors.loginCallbackUrl }}
                </div>
                <p class="form-hint">
                  <i class="ri-information-line"></i>
                  開發環境可使用 ngrok URL（例如：<code>https://xxxx.ngrok-free.app</code>）
                  <br>
                  自動生成值：<code>{{ generatedCallbackUrl }}</code>
                </p>
                <div class="callback-url-alert">
                  <div class="alert-icon">
                    <i class="ri-alert-fill"></i>
                  </div>
                  <div class="alert-content">
                    <div class="alert-title">
                      <strong>重要提醒</strong>
                    </div>
                    <div class="alert-message">
                      請將此 URL 複製到 <strong>LINE Developers Console</strong> 的 <strong>LINE Login</strong> 設定中的 <strong>Callback URL</strong> 欄位貼上
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 步驟五：LIFF 設定 -->
        <div v-if="currentStep === 4" class="step-content">
          <div class="form-section">
            <h2 class="section-title">
              <i class="ri-apps-2-line"></i>
              步驟五：LIFF 設定
            </h2>
            <p class="section-description">設定共用入口與電商模組的 LIFF 應用程式</p>

            <!-- 共用入口 LIFF -->
            <div class="form-group-section form-group-section--common-liff">
              <div class="section-note">
                <i class="ri-information-line"></i>
                <span>LIFF 應用程式設定</span>
              </div>
              
              <div class="form-group">
                <!-- LIFF Endpoint URL -->
                <div class="liff-endpoint-url liff-endpoint-url--above">
                  <label class="endpoint-label">1. 請先複製下方 LIFF Endpoint URL</label>
                  <div class="input-group">
                    <input
                      v-model="commonLiffEndpointUrl"
                      type="text"
                      class="form-control"
                      :placeholder="getLiffEndpointUrl(form.commonLiffId) || 'https://...'"
                    />
                    <button
                      type="button"
                      class="btn btn-secondary"
                      @click="useGeneratedCommonLiffEndpointUrl"
                      title="使用自動生成的 URL"
                    >
                      <i class="ri-refresh-line"></i>
                      自動生成
                    </button>
                    <button
                      type="button"
                      class="btn btn-secondary"
                      @click="copyToClipboard(commonLiffEndpointUrl || getLiffEndpointUrl(form.commonLiffId), 'LIFF Endpoint URL')"
                    >
                      <i class="ri-file-copy-line"></i>
                      複製
                    </button>
                  </div>
                  <p class="form-hint">
                    <i class="ri-information-line"></i>
                    自動生成值：<code>{{ getLiffEndpointUrl(form.commonLiffId) || '—' }}</code>
                  </p>
                  <p class="form-hint form-hint-endpoint-warning">
                    <i class="ri-error-warning-fill liff-endpoint-hint-icon" aria-hidden="true" />
                    <span>2. 請將此 URL 複製到 LINE Developers Console 的 LIFF 應用程式中的 Endpoint URL 欄位貼上<br>完成 LIFF 設定後，將 LIFF ID 輸入到下方的「共用入口 LIFF ID」欄位</span>
                  </p>
                </div>

                <label class="required">3. 共用入口 LIFF ID</label>
                <input
                  v-model="form.commonLiffId"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': errors.commonLiffId }"
                  placeholder="例如：1234567890-abcdefgh"
                  required
                  @blur="validateCommonLiffId"
                />
                <div v-if="errors.commonLiffId" class="invalid-feedback">
                  {{ errors.commonLiffId }}
                </div>
                <p class="form-hint">共用入口的 LIFF 應用程式 ID，格式必須包含 - 符號（例如：1234567890-abcdefgh）</p>
              </div>
            </div>

            <!-- 電商模組 LIFF 設定 -->
            <div class="form-group-section form-group-section--ecommerce-liff">
              <div class="section-note">
                <i class="ri-store-2-line"></i>
                <span>電商模組 LIFF 應用程式設定（選配）：<strong>未選購購物中心功能的用戶不需填寫</strong></span>
              </div>

              <div class="form-group">
                <!-- LIFF Endpoint URL -->
                <div class="liff-endpoint-url liff-endpoint-url--above">
                  <label class="endpoint-label">1. 請先複製下方 LIFF Endpoint URL</label>
                  <div class="input-group">
                    <input
                      v-model="ecommerceLiffEndpointUrl"
                      type="text"
                      class="form-control"
                      :placeholder="getEcommerceLiffEndpointUrl(form.ecommerceLiffId) || 'https://...'"
                    />
                    <button
                      type="button"
                      class="btn btn-secondary"
                      @click="useGeneratedEcommerceLiffEndpointUrl"
                      title="使用自動生成的 URL"
                    >
                      <i class="ri-refresh-line"></i>
                      自動生成
                    </button>
                    <button
                      type="button"
                      class="btn btn-secondary"
                      @click="copyToClipboard(ecommerceLiffEndpointUrl || getEcommerceLiffEndpointUrl(form.ecommerceLiffId), 'LIFF Endpoint URL')"
                    >
                      <i class="ri-file-copy-line"></i>
                      複製
                    </button>
                  </div>
                  <p class="form-hint">
                    <i class="ri-information-line"></i>
                    自動生成值：<code>{{ getEcommerceLiffEndpointUrl(form.ecommerceLiffId) || '—' }}</code>
                  </p>
                  <p class="form-hint form-hint-endpoint-warning">
                    <i class="ri-error-warning-fill liff-endpoint-hint-icon" aria-hidden="true" />
                    <span>2. 請將此 URL 複製到 LINE Developers Console 的 LIFF 應用程式中的 Endpoint URL 欄位貼上<br>完成 LIFF 設定後，將 LIFF ID 輸入到下方的「電商模組專用 LIFF ID」欄位</span>
                  </p>
                </div>

                <label>3. 電商模組專用 LIFF ID</label>
                <input
                  v-model="form.ecommerceLiffId"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': errors.ecommerceLiffId }"
                  placeholder="例如：1234567890-abcdefgh"
                  @blur="validateEcommerceLiffId"
                />
                <div v-if="errors.ecommerceLiffId" class="invalid-feedback">
                  {{ errors.ecommerceLiffId }}
                </div>
                <p class="form-hint">電商模組專用的 LIFF 應用程式 ID（選填），格式必須包含 - 符號（例如：1234567890-abcdefgh）</p>
              </div>

              <FormTipBox title="電商前台網域與 DNS 說明" spacing="section">
                <p class="seo-help-intro">
                  網址請到<strong>商城設定 → 一般設定 → 前台網址</strong>修改。
                </p>
                <ol class="line-storefront-tip-list">
                  <li>
                    <strong>用系統提供的網址：</strong>到商城設定按「套用預設網址」，再按儲存。不用再到網域商設定。系統預設為
                    <code>{ChannelId}.mall.lockline.io</code>（測試為 <code>{ChannelId}.dev-mall.lockline.io</code>）。
                  </li>
                  <li>
                    <strong>用自己的網域：</strong>到商城設定填完整網址（開頭要有 <code>https://</code>），再按儲存。然後到購買網域的地方新增一筆 CNAME，目標填這個（不要加 https://）：
                    <div class="cname-copy-row">
                      <code class="cname-copy-value">{{ ecommerceEdgeHost }}</code>
                      <button
                        type="button"
                        class="btn btn-secondary"
                        @click="copyToClipboard(ecommerceEdgeHost, 'CNAME 目標')"
                      >
                        <i class="ri-file-copy-line"></i>
                        複製
                      </button>
                    </div>
                  </li>
                  <li>
                    <template v-if="canLinkCommerceSettings">
                      如要修改網址，或改用自行購買的網域，請
                      <router-link :to="{ name: 'ClientCommerceSettings' }">前往商城設定</router-link>
                    </template>
                    <template v-else>
                      如要修改網址，或改用自行購買的網域，請由商店管理員到「商城設定」修改。
                    </template>
                  </li>
                </ol>
              </FormTipBox>
            </div>
          </div>
        </div>


        <!-- 測試結果顯示區塊（編輯模式下所有步驟都顯示） -->
        <div v-if="isEdit && configData.lastTestedAt" class="test-result-section">
          <div class="form-section test-result-form-section">
            <div class="test-result-section-head">
              <h2 class="section-title test-result-section-title">
                <span class="test-result-section-icon" aria-hidden="true">
                  <i class="ri-pulse-line"></i>
                </span>
                測試結果
              </h2>
              <p class="test-result-section-desc">最近一次「測試連線」的狀態與 Bot 資訊</p>
            </div>
            <div class="test-result-card">
              <div class="test-result-summary">
                <div
                  class="test-result-pill"
                  :class="{
                    'test-result-pill--success': configData.lastTestResult === 'success',
                    'test-result-pill--failed': configData.lastTestResult === 'failed',
                    'test-result-pill--neutral': configData.lastTestResult !== 'success' && configData.lastTestResult !== 'failed'
                  }"
                >
                  <i
                    class="test-result-pill__icon"
                    :class="configData.lastTestResult === 'success' ? 'ri-checkbox-circle-fill' : (configData.lastTestResult === 'failed' ? 'ri-close-circle-fill' : 'ri-question-line')"
                    aria-hidden="true"
                  />
                  <span v-if="configData.lastTestResult === 'success'">測試成功</span>
                  <span v-else-if="configData.lastTestResult === 'failed'">測試失敗</span>
                  <span v-else>未測試</span>
                </div>
                <div class="test-result-meta">
                  <i class="ri-time-line" aria-hidden="true" />
                  <span class="test-result-meta__label">最後測試</span>
                  <time class="test-result-meta__value" :datetime="configData.lastTestedAt">{{ formatDateTime(configData.lastTestedAt) }}</time>
                </div>
              </div>

              <div v-if="configData.lastTestResult === 'failed' && configData.lastTestError" class="test-result-panel test-result-panel--error">
                <div class="test-result-panel__head">
                  <i class="ri-error-warning-fill" aria-hidden="true" />
                  <span>錯誤訊息</span>
                </div>
                <pre class="test-result-error-body">{{ configData.lastTestError }}</pre>
              </div>

              <div v-if="configData.lastTestResult === 'success' && configData.botInfo" class="test-result-panel test-result-panel--bot">
                <div class="test-result-panel__head">
                  <i class="ri-robot-2-line" aria-hidden="true" />
                  <span>Bot 資訊</span>
                </div>
                <div class="test-result-kv">
                  <div class="test-result-kv__row">
                    <span class="test-result-kv__k">顯示名稱</span>
                    <span class="test-result-kv__v">{{ configData.botInfo.displayName || '—' }}</span>
                  </div>
                  <div class="test-result-kv__row">
                    <span class="test-result-kv__k">Bot User ID</span>
                    <code class="test-result-kv__code">{{ configData.botInfo.userId || '—' }}</code>
                  </div>
                  <div class="test-result-kv__row">
                    <span class="test-result-kv__k">Bot Basic ID</span>
                    <code class="test-result-kv__code">{{ configData.botInfo.basicId || '—' }}</code>
                  </div>
                  <div v-if="configData.botInfo.pictureUrl" class="test-result-kv__row test-result-kv__row--avatar">
                    <span class="test-result-kv__k">頭像</span>
                    <img :src="configData.botInfo.pictureUrl" alt="Bot 頭像" class="test-result-avatar" width="48" height="48" />
                  </div>
                </div>
              </div>

              <div v-else-if="configData.botUserId" class="test-result-panel test-result-panel--bot">
                <div class="test-result-panel__head">
                  <i class="ri-database-2-line" aria-hidden="true" />
                  <span>已儲存的 Bot 資訊</span>
                </div>
                <p class="test-result-panel__hint">以下為資料庫紀錄，重新整理頁面後仍會顯示。</p>
                <div class="test-result-kv">
                  <div v-if="configData.botDisplayName" class="test-result-kv__row">
                    <span class="test-result-kv__k">顯示名稱</span>
                    <span class="test-result-kv__v">{{ configData.botDisplayName }}</span>
                  </div>
                  <div class="test-result-kv__row">
                    <span class="test-result-kv__k">Bot User ID</span>
                    <code class="test-result-kv__code">{{ configData.botUserId }}</code>
                  </div>
                  <div v-if="configData.botBasicId" class="test-result-kv__row">
                    <span class="test-result-kv__k">Bot Basic ID</span>
                    <code class="test-result-kv__code">{{ configData.botBasicId }}</code>
                  </div>
                  <div v-if="configData.botPictureUrl" class="test-result-kv__row test-result-kv__row--avatar">
                    <span class="test-result-kv__k">頭像</span>
                    <img :src="configData.botPictureUrl" alt="Bot 頭像" class="test-result-avatar" width="48" height="48" />
                  </div>
                </div>
              </div>

              <div v-else class="test-result-panel test-result-panel--hint">
                <i class="ri-lightbulb-line test-result-hint__icon" aria-hidden="true" />
                <p class="test-result-hint__text">
                  點一下「<strong>測試連線</strong>」，系統會記下此帳號的 Bot ID，讓 ChatMate 在群組被 <strong>@mention</strong> 時能正確回應。
                </p>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { lineService } from '@/api/modules/line.js';
import { clientService } from '@/api/modules/clients.js';
import { commerceSettingsApi } from '@/api/modules/commerceSettings.js';
import { showError, showSuccess } from '@/utils/swal.js';
import authService from '@/helpers/authservice/authService.js';
import { isClientOwner } from '@/utils/clientAccount.js';
import FormTipBox from '@/components/form/FormTipBox.vue';

export default {
  name: 'LineConfigCreate',
  components: {
    FormTipBox,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const isEdit = computed(() => !!route.params.id);
    const { pageTitle } = usePageTitle(isEdit.value ? '編輯 LINE 設定' : '新增 LINE 設定');

    const form = reactive({
      clientId: '', // 客戶 ID（只有 system 和 platform 帳號需要）
      configName: '',
      messagingChannelId: '',
      messagingChannelSecret: '',
      messagingBotBasicId: '',
      messagingAccessToken: '',
      messagingAccessTokenExpiresAt: '',
      loginChannelId: '',
      loginChannelSecret: '',
      loginCallbackUrl: '', // 允許手動輸入（用於 ngrok 等開發環境）
      commonLiffId: '',
      ecommerceLiffId: '',
      ecommerceLiffCustomDomain: '', // 電商模組自訂 domain
      isActive: true
      // status 欄位由系統自動管理，不需要用戶手動設定
    });

    const configData = ref({
      hasMessagingChannelSecret: false,
      hasMessagingAccessToken: false,
      hasLoginChannelSecret: false
    });
    const errors = reactive({});
    const submitting = ref(false);
    const testing = ref(false);
    const loading = ref(false);
    const clients = ref([]);
    const loadingClients = ref(false);
    const clientSearchKeyword = ref('');
    const showClientList = ref(false);
    const selectedClient = ref(null);
    
    // 控制敏感資訊的顯示/隱藏
    const showMessagingChannelSecret = ref(false);
    const showMessagingAccessToken = ref(false);
    const showLoginChannelSecret = ref(false);

    // LIFF Endpoint URL（與後端 commonLiffEndpointUrl / ecommerceLiffEndpointUrl 同步；可手動改寫；點「自動生成」帶入推算值）
    const commonLiffEndpointUrl = ref('');
    const ecommerceLiffEndpointUrl = ref('');
    
    // 檢查是否需要選擇客戶
    const needsClientSelection = computed(() => {
      const accountType = authService.getAccountType();
      return ['system', 'platform'].includes(accountType) && !isEdit.value;
    });

    const commerceStorefrontHostname = ref('');
    const commerceStorefrontPublicBaseUrl = ref('');
    const suggestedStorefrontHost = ref('');

    const canLinkCommerceSettings = computed(() => isClientOwner(authService.getCurrentUser()));

    const displayStorefrontPublicBaseUrl = computed(() =>
      commerceStorefrontPublicBaseUrl.value.trim()
    );

    /** 自購網域時，CNAME 要指向的系統網址（與 Messaging Channel ID 連動） */
    const ecommerceEdgeHost = computed(() => {
      const fromSettings = suggestedStorefrontHost.value.trim();
      if (fromSettings) return fromSettings;
      const channelId = (form.messagingChannelId || '').trim();
      if (!channelId) return '{Channel ID}.mall.lockline.io';
      return `${channelId}.mall.lockline.io`;
    });

    const refreshCommerceStorefrontFields = async () => {
      commerceStorefrontHostname.value = '';
      commerceStorefrontPublicBaseUrl.value = '';
      suggestedStorefrontHost.value = '';
      const at = authService.getAccountType();
      if (!['client'].includes(at)) return;
      try {
        const res = await commerceSettingsApi.getSettings();
        const s = res?.data ?? {};
        commerceStorefrontHostname.value =
          s.storefrontHostname != null ? String(s.storefrontHostname).trim() : '';
        commerceStorefrontPublicBaseUrl.value =
          s.storefrontPublicBaseUrl != null ? String(s.storefrontPublicBaseUrl).trim() : '';
        suggestedStorefrontHost.value = '';
        const suggested = s.suggestedDefaultStorefrontUrl != null
          ? String(s.suggestedDefaultStorefrontUrl).trim()
          : '';
        if (suggested) {
          try {
            suggestedStorefrontHost.value = new URL(suggested).host;
          } catch {
            suggestedStorefrontHost.value = '';
          }
        }
      } catch (e) {
        console.warn('[LineConfig] 無法載入商城前台網域設定:', e);
      }
    };

    // 生成 Webhook URL
    // 規則：https://{api base url}/api/v1/webhooks/line/{line message channel id}
    const generatedWebhookUrl = computed(() => {
      if (!form.messagingChannelId) return '';
      
      // 取得 API base URL
      let apiBaseUrl = process.env.VUE_APP_API_BASE_URL;
      
      // 如果是相對路徑（以 / 開頭），轉換為完整 URI
      if (!apiBaseUrl || apiBaseUrl.startsWith('/')) {
        // 使用當前頁面的 origin，但將端口改為 3000（API 服務端口）
        const origin = window.location.origin;
        const apiOrigin = origin.replace(/:8080|:8081|:8082/, ':3000');
        
        // 如果是相對路徑，加上協議和主機
        if (apiBaseUrl && apiBaseUrl.startsWith('/')) {
          apiBaseUrl = apiOrigin + apiBaseUrl;
        } else {
          apiBaseUrl = apiOrigin + '/api/v1';
        }
      }
      
      // 確保 apiBaseUrl 是完整的 URI（包含協議）
      if (!apiBaseUrl.startsWith('http://') && !apiBaseUrl.startsWith('https://')) {
        apiBaseUrl = 'http://' + apiBaseUrl;
      }
      
      // 移除末尾的 /api/v1（如果有的話），因為我們會在後面加上完整路徑
      apiBaseUrl = apiBaseUrl.replace(/\/api\/v1$/, '');
      
      return `${apiBaseUrl}/api/v1/webhooks/line/${form.messagingChannelId}`;
    });

    // 生成 Callback URL
    // 規則：根據客戶的會員模式，base URL 與 LIFF 相同（由 getLiffBaseUrl() 決定）
    // LOCKLINE會員：https://{liff base url}/lockline/linelogin/{line message channel id}
    // WIXTAR會員：https://{liff base url}/wixtar/linelogin/{line message channel id}
    const generatedCallbackUrl = computed(() => {
      if (!form.messagingChannelId) return '';
      
      const baseUrl = getLiffBaseUrl();
      const channelId = form.messagingChannelId;
      const memberMode = clientMemberSystemType.value || 'LOCKLINE';
      
      // 根據會員模式生成不同的 URL
      if (memberMode === 'WIXTAR') {
        return `${baseUrl}/wixtar/linelogin/${channelId}`;
      } else {
        // 預設為 LOCKLINE
        return `${baseUrl}/lockline/linelogin/${channelId}`;
      }
    });
    
    // 過濾客戶列表
    const filteredClients = computed(() => {
      if (!clientSearchKeyword.value.trim()) {
        return clients.value;
      }
      const keyword = clientSearchKeyword.value.toLowerCase();
      return clients.value.filter(client => 
        client.clientName.toLowerCase().includes(keyword) ||
        client.clientCode.toLowerCase().includes(keyword)
      );
    });
    
    // 展開/收合步驟詳細說明
    const expandedSteps = reactive({
      step3: false,
      step4: false,
      step5: false,
      step6: false,
      businessAccount: false  // LINE 商用帳號說明區塊
    });
    
    const toggleStepDetail = (stepKey) => {
      expandedSteps[stepKey] = !expandedSteps[stepKey];
    };
    
    // 編輯模式下的客戶資訊
    const editClientInfo = ref(null);

    // 當前客戶資訊（客戶帳號自動帶入或編輯模式顯示）
    const currentClientInfo = computed(() => {
      // 編輯模式：顯示編輯的客戶資訊
      if (isEdit.value && editClientInfo.value) {
        return editClientInfo.value;
      }
      
      // 新增模式：只有客戶帳號才顯示
      if (needsClientSelection.value) {
        return null; // 系統/平台帳號不需要顯示
      }
      
      const user = authService.getCurrentUser();
      if (user && user.clientId) {
        // 優先從客戶列表中查找客戶資訊（更完整）
        const client = clients.value.find(c => c.id === user.clientId);
        if (client) {
          return {
            clientId: client.id,
            clientName: client.clientName,
            clientCode: client.clientCode
          };
        }
        // 如果找不到，使用用戶資訊
        return {
          clientId: user.clientId,
          clientName: user.clientName || user.name || '當前客戶',
          clientCode: user.clientCode || ''
        };
      }
      return null;
    });

    // 步驟控制
    const currentStep = ref(0);
    const steps = computed(() => {
      return [
        { label: '申請需知', key: 'guide' },
        { label: '基本設定', key: 'basic' },
        { label: 'Messaging API 設定', key: 'messaging' },
        { label: 'LINE Login 設定', key: 'login' },
        { label: 'LIFF 設定', key: 'liff' }
      ];
    });

    // 獲取客戶列表
    const fetchClients = async () => {
      loadingClients.value = true;
      try {
        const response = await clientService.getClients({
          page: 1,
          pageSize: 100, // 最大限制 100
          status: 'active' // 只獲取啟用的客戶
        });
        
        if (response && response.success && response.data) {
          if (response.data.items) {
            clients.value = response.data.items;
          } else if (Array.isArray(response.data)) {
            // 如果 data 本身就是陣列
            clients.value = response.data;
          } else {
            clients.value = [];
          }
          
              // 新增模式：如果是客戶帳號，自動帶入客戶資料
              if (!isEdit.value && !needsClientSelection.value) {
                const user = authService.getCurrentUser();
                if (user && user.clientId) {
                  const client = clients.value.find(c => c.id === user.clientId);
                  if (client) {
                    form.clientId = client.id;
                    selectedClient.value = client;
                    // 載入客戶會員模式
                    fetchClientMemberSystemType(client.id);
              }
            }
          }
        } else {
          await showError('載入失敗', response?.message || '獲取客戶列表失敗');
        }
      } catch (err) {
        await showError('載入失敗', err.response?.data?.message || err.message || '獲取客戶列表失敗');
      } finally {
        loadingClients.value = false;
      }
    };

    // 處理客戶搜尋
    const handleClientSearch = () => {
      showClientList.value = true;
    };

    // 選擇客戶
    const selectClient = (client) => {
      form.clientId = client.id;
      selectedClient.value = client;
      clientSearchKeyword.value = '';
      showClientList.value = false;
      // 清除錯誤
      delete errors.clientId;
      // 載入客戶會員模式
      fetchClientMemberSystemType(client.id);
    };

    // 清除客戶選擇
    const clearClientSelection = () => {
      form.clientId = '';
      selectedClient.value = null;
      clientSearchKeyword.value = '';
      showClientList.value = false;
    };

    const fetchConfig = async () => {
      if (!isEdit.value) return;

      loading.value = true;
      try {
        // 編輯時請求解密後的值
        const response = await lineService.getConfig(route.params.id, true);
        if (response && response.success && response.data) {
          const data = response.data;
          configData.value = data;

          // 填充表單
          form.clientId = data.clientId || ''; // 編輯時不需要選擇客戶
          form.configName = data.configName || '';
          form.messagingChannelId = data.messagingApi?.channelId || '';
          // 敏感資訊：直接顯示解密後的值
          form.messagingChannelSecret = data.messagingApi?.channelSecret || '';
          form.messagingBotBasicId = data.messagingApi?.botBasicId || '';
          form.messagingAccessToken = data.messagingApi?.accessToken || '';
          form.messagingAccessTokenExpiresAt = data.messagingApi?.accessTokenExpiresAt 
            ? new Date(data.messagingApi.accessTokenExpiresAt).toISOString().slice(0, 16)
            : '';
          form.loginChannelId = data.lineLogin?.channelId || '';
          form.loginChannelSecret = data.lineLogin?.channelSecret || '';
          // 回填 loginCallbackUrl（如果有的話），否則使用自動生成的值
          form.loginCallbackUrl = data.lineLogin?.callbackUrl || generatedCallbackUrl.value || '';
          // 處理 LIFF IDs：從根層級的 liffIds 陣列中提取共用入口和電商模組專用
          const liffIds = data.liffIds || [];
          form.commonLiffId = liffIds[0] || '';
          form.ecommerceLiffId = liffIds[1] || '';
          // 電商模組自訂 domain（如果有的話）
          form.ecommerceLiffCustomDomain = data.ecommerceLiffCustomDomain || '';
          form.isActive = data.isActive !== undefined ? data.isActive : true;
          // status 欄位由系統自動管理，不需要回填

          // LIFF Endpoint URL：僅回填 API 已儲存的值，不寫入自動生成（避免編輯畫面覆蓋 DB）
          const storedCommonEp =
            typeof data.commonLiffEndpointUrl === 'string' ? data.commonLiffEndpointUrl.trim() : '';
          const storedEcomEp =
            typeof data.ecommerceLiffEndpointUrl === 'string' ? data.ecommerceLiffEndpointUrl.trim() : '';
          commonLiffEndpointUrl.value = storedCommonEp;
          ecommerceLiffEndpointUrl.value = storedEcomEp;
          
          // 載入客戶會員模式（用於生成 LIFF Endpoint URL）
          if (data.clientId) {
            fetchClientMemberSystemType(data.clientId);
          }

          await refreshCommerceStorefrontFields();
          
          // 測試結果資訊（如果有的話）
          if (data.lastTestedAt) {
            configData.value.lastTestedAt = data.lastTestedAt;
            configData.value.lastTestResult = data.lastTestResult;
            configData.value.lastTestError = data.lastTestError;
          }

          // Bot 資訊（從 DB 載入，測試連線後永久儲存）
          if (data.botUserId) {
            configData.value.botUserId      = data.botUserId;
            configData.value.botDisplayName = data.botDisplayName || null;
            configData.value.botBasicId     = data.botBasicId     || null;
            configData.value.botPictureUrl  = data.botPictureUrl  || null;
          }

          // 如果有 clientId，載入客戶資訊用於顯示
          if (data.clientId) {
            const accountType = authService.getAccountType();
            const isClientAccount = ['client'].includes(accountType);
            
            if (isClientAccount) {
              // 客戶帳號：直接從當前用戶資訊獲取客戶資訊
              const user = authService.getCurrentUser();
              if (user && user.clientId === data.clientId) {
                editClientInfo.value = {
                  clientId: user.clientId,
                  clientName: user.clientName || user.name || '當前客戶',
                  clientCode: user.clientCode || ''
                };
              }
            } else {
              // 系統/平台帳號：從客戶列表中查找
              // 先嘗試從已載入的客戶列表中查找
              const client = clients.value.find(c => c.id === data.clientId);
              if (client) {
                editClientInfo.value = {
                  clientId: client.id,
                  clientName: client.clientName,
                  clientCode: client.clientCode
                };
              } else {
                // 如果客戶列表還沒載入或找不到，載入客戶列表
                await fetchClients();
                const foundClient = clients.value.find(c => c.id === data.clientId);
                if (foundClient) {
                  editClientInfo.value = {
                    clientId: foundClient.id,
                    clientName: foundClient.clientName,
                    clientCode: foundClient.clientCode
                  };
                } else {
                  // 如果還是找不到，至少顯示 clientId
                  editClientInfo.value = {
                    clientId: data.clientId,
                    clientName: '客戶 ID: ' + data.clientId.substring(0, 8) + '...',
                    clientCode: ''
                  };
                }
              }
            }
          }
        } else {
          await showError('載入失敗', response?.message || '獲取 LINE 設定失敗');
          router.push({ name: getListRouteName() });
        }
      } catch (err) {
        console.error('獲取 LINE 設定失敗:', err);
        const errorMessage = err.response?.data?.message || err.message || '獲取 LINE 設定失敗';
        await showError('載入失敗', errorMessage);
        router.push({ name: getListRouteName() });
      } finally {
        loading.value = false;
      }
    };

    // 驗證當前步驟
    const validateCurrentStep = () => {
      const newErrors = {};
      
      // 清除所有錯誤
      Object.keys(errors).forEach(key => delete errors[key]);

      if (currentStep.value === 0) {
        // 步驟零：申請需知（不需要驗證，只是資訊展示）
        return true;
      } else if (currentStep.value === 1) {
        // 步驟一：基本設定
        // 如果需要選擇客戶，驗證客戶選擇
        if (needsClientSelection.value && !form.clientId) {
          newErrors.clientId = '請選擇客戶';
        }
        // 驗證設定名稱
        if (!form.configName.trim()) {
          newErrors.configName = '設定名稱為必填';
        }
      } else if (currentStep.value === 2) {
        // 步驟二：Messaging API 設定
        if (!form.messagingChannelId.trim()) {
          newErrors.messagingChannelId = 'Messaging Channel ID 為必填';
        } else if (!/^\d+$/.test(form.messagingChannelId.trim())) {
          newErrors.messagingChannelId = 'Channel ID 必須是 0-9 的數字';
        }
        if (!form.messagingChannelSecret.trim() && !isEdit.value) {
          newErrors.messagingChannelSecret = 'Messaging Channel Secret 為必填';
        }
        if (!form.messagingBotBasicId.trim()) {
          newErrors.messagingBotBasicId = 'Bot Basic ID 為必填';
        } else if (!/^@[a-zA-Z0-9_-]+$/.test(form.messagingBotBasicId.trim())) {
          newErrors.messagingBotBasicId = 'Bot Basic ID 必須以 @ 開頭';
        }
        if (!form.messagingAccessToken.trim() && !isEdit.value) {
          newErrors.messagingAccessToken = 'Channel Access Token 為必填';
        }
      } else if (currentStep.value === 3) {
        // 步驟三：LINE Login 設定
        if (!form.loginChannelId.trim()) {
          newErrors.loginChannelId = 'LINE Login Channel ID 為必填';
        } else if (!/^\d+$/.test(form.loginChannelId.trim())) {
          newErrors.loginChannelId = 'Channel ID 必須是 0-9 的數字';
        }
        if (!form.loginChannelSecret.trim() && !isEdit.value) {
          newErrors.loginChannelSecret = 'LINE Login Channel Secret 為必填';
        }
        // Callback URL 由系統自動生成，不需要驗證
      } else if (currentStep.value === 4) {
        // 步驟五：LIFF 設定
        if (!form.commonLiffId.trim()) {
          newErrors.commonLiffId = '共用入口 LIFF ID 為必填';
        } else if (!form.commonLiffId.trim().includes('-')) {
          newErrors.commonLiffId = 'LIFF ID 格式錯誤，必須包含 - 符號（例如：1234567890-abcdefgh）';
        }
        if (form.ecommerceLiffId.trim() && !form.ecommerceLiffId.trim().includes('-')) {
          newErrors.ecommerceLiffId = 'LIFF ID 格式錯誤，必須包含 - 符號（例如：1234567890-abcdefgh）';
        }
        if (form.commonLiffId.trim() && form.ecommerceLiffId.trim() &&
            form.commonLiffId.trim() === form.ecommerceLiffId.trim()) {
          newErrors.commonLiffId = '共用入口 LIFF ID 不能與電商模組 LIFF ID 相同';
          newErrors.ecommerceLiffId = '電商模組 LIFF ID 不能與共用入口 LIFF ID 相同';
        }
      }

      Object.assign(errors, newErrors);
      return Object.keys(newErrors).length === 0;
    };

    // 驗證整個表單
    const validateForm = () => {
      // 驗證所有步驟
      for (let i = 0; i < steps.value.length; i++) {
        currentStep.value = i;
        if (!validateCurrentStep()) {
          return false;
        }
      }
      return true;
    };

    // 是否可以進入下一步（只讀取，不修改狀態，避免遞迴更新）
    // 檢查是否可以導航到指定步驟
    const canNavigateToStep = (targetStep) => {
      // 編輯模式下，所有步驟都可以導航（因為設定已經存在）
      if (isEdit.value) {
        return true;
      }
      // 新增模式下，只能導航到當前步驟或已完成的步驟
      return targetStep <= currentStep.value || targetStep === 0;
    };

    // 導航到指定步驟
    const goToStep = (targetStep) => {
      if (!canNavigateToStep(targetStep)) {
        return; // 不能導航到未完成的步驟
      }
      currentStep.value = targetStep;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const canProceedToNextStep = computed(() => {
      if (currentStep.value === 0) {
        // 步驟零：申請需知（不需要驗證）
        return true;
      } else if (currentStep.value === 1) {
        // 步驟一：基本設定
        // 如果需要選擇客戶，驗證客戶選擇
        if (needsClientSelection.value && !form.clientId) {
          return false;
        }
        // 驗證設定名稱
        return !!form.configName.trim();
      } else if (currentStep.value === 2) {
        // 步驟二：Messaging API 設定
        const channelIdValid = form.messagingChannelId.trim() && /^\d+$/.test(form.messagingChannelId.trim());
        const botBasicIdValid = form.messagingBotBasicId.trim() && /^@[a-zA-Z0-9_-]+$/.test(form.messagingBotBasicId.trim());
        return !!(
          channelIdValid &&
          (isEdit.value || form.messagingChannelSecret.trim()) &&
          botBasicIdValid &&
          (isEdit.value || form.messagingAccessToken.trim())
        );
      } else if (currentStep.value === 3) {
        // 步驟三：LINE Login 設定
        const loginChannelIdValid = form.loginChannelId.trim() && /^\d+$/.test(form.loginChannelId.trim());
        if (!loginChannelIdValid) return false;
        if (!isEdit.value && !form.loginChannelSecret.trim()) return false;
        return true;
      } else if (currentStep.value === 4) {
        // 步驟五：LIFF 設定
        const commonLiffIdValid = form.commonLiffId.trim() && form.commonLiffId.trim().includes('-');
        if (!commonLiffIdValid) return false;
        if (form.ecommerceLiffId.trim() && !form.ecommerceLiffId.trim().includes('-')) return false;
        return true;
      }
      return true;
    });

    // 下一步
    const nextStep = () => {
      if (validateCurrentStep() && currentStep.value < steps.value.length - 1) {
        currentStep.value++;
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

    // 上一步
    const previousStep = () => {
      if (currentStep.value > 0) {
        currentStep.value--;
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

    const handleSubmit = async () => {
      if (!validateForm()) {
        // 如果有錯誤，跳轉到第一個有錯誤的步驟
        for (let i = 0; i < steps.value.length; i++) {
          currentStep.value = i;
          if (!validateCurrentStep()) {
            break;
          }
        }
        return;
      }

      submitting.value = true;
      try {
        const submitData = {
          configName: form.configName.trim(),
          messagingChannelId: form.messagingChannelId.trim(),
          messagingBotBasicId: form.messagingBotBasicId.trim(),
          loginChannelId: form.loginChannelId.trim(),
          loginCallbackUrl: form.loginCallbackUrl.trim() || generatedCallbackUrl.value, // 優先使用手動輸入的值，否則使用自動生成
          liffIds: [
            form.commonLiffId.trim(),
            form.ecommerceLiffId.trim()
          ].filter(id => id), // 過濾空值
          ecommerceLiffCustomDomain:
            commerceStorefrontHostname.value.trim() || form.ecommerceLiffCustomDomain.trim() || null,
          commonLiffEndpointUrl: commonLiffEndpointUrl.value.trim() || null,
          ecommerceLiffEndpointUrl: ecommerceLiffEndpointUrl.value.trim() || null,
          isActive: form.isActive
          // status 欄位由系統自動管理，建立時預設為 'inactive'
        };
        
        // 只有 system 和 platform 帳號需要傳遞 clientId
        if (needsClientSelection.value && form.clientId) {
          submitData.clientId = form.clientId;
        }

        // 只有在新增或更新時才包含敏感資訊
        if (!isEdit.value || form.messagingChannelSecret.trim()) {
          submitData.messagingChannelSecret = form.messagingChannelSecret.trim();
        }

        if (!isEdit.value || form.messagingAccessToken.trim()) {
          submitData.messagingAccessToken = form.messagingAccessToken.trim();
        }

        if (form.messagingAccessTokenExpiresAt) {
          submitData.messagingAccessTokenExpiresAt = new Date(form.messagingAccessTokenExpiresAt).toISOString();
        }

        if (!isEdit.value || form.loginChannelSecret.trim()) {
          submitData.loginChannelSecret = form.loginChannelSecret.trim();
        }

        let response;
        if (isEdit.value) {
          response = await lineService.updateConfig(route.params.id, submitData);
        } else {
          response = await lineService.createConfig(submitData);
        }

        if (response && response.success) {
          await showSuccess(
            isEdit.value ? '更新成功' : '建立成功',
            isEdit.value ? 'LINE 設定已更新' : 'LINE 設定已建立'
          );
          router.push({ name: getListRouteName() });
        } else {
          await showError(
            isEdit.value ? '更新失敗' : '建立失敗',
            response?.message || (isEdit.value ? '更新 LINE 設定失敗' : '建立 LINE 設定失敗')
          );
        }
      } catch (err) {
        console.error('提交失敗:', err);
        const errorMessage = err.response?.data?.message || err.message || '操作失敗';
        await showError('操作失敗', errorMessage);
      } finally {
        submitting.value = false;
      }
    };

    const handleTest = async () => {
      if (!isEdit.value) {
        await showError('無法測試', '請先儲存設定後再測試連線');
        return;
      }

      testing.value = true;
      try {
        const response = await lineService.testConnection(route.params.id);
        if (response && response.success) {
          if (response.data.success) {
            await showSuccess('測試成功', response.data.message || 'LINE 連線測試成功');
            // 重新載入設定以獲取最新的測試結果（含 botUserId）
            await fetchConfig();
            // fetchConfig 覆蓋後再補上即時 botInfo（DB 不存完整 botInfo）
            if (response.data.botInfo) {
              configData.value.botInfo = response.data.botInfo;
            }
          } else {
            await showError('測試失敗', response.data.message || 'LINE 連線測試失敗');
            // 更新測試結果到 configData
            configData.value.lastTestedAt = new Date().toISOString();
            configData.value.lastTestResult = 'failed';
            configData.value.lastTestError = response.data.message || '測試失敗';
            // 重新載入設定以獲取最新的測試結果
            await fetchConfig();
          }
        } else {
          await showError('測試失敗', response?.message || 'LINE 連線測試失敗');
        }
      } catch (err) {
        console.error('測試連線失敗:', err);
        const errorMessage = err.response?.data?.message || err.message || '測試連線失敗';
        await showError('測試失敗', errorMessage);
      } finally {
        testing.value = false;
      }
    };

    // 根據當前路由獲取列表頁路由名稱（統一使用 ClientLineConfigList）
    const getListRouteName = () => {
      return 'ClientLineConfigList';
    };

    const handleCancel = () => {
      router.push({ name: getListRouteName() });
    };

    // 格式化日期時間
    const formatDateTime = (dateTimeString) => {
      if (!dateTimeString) return '-';
      const date = new Date(dateTimeString);
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    };


    const copyToClipboard = async (text, label) => {
      try {
        await navigator.clipboard.writeText(text);
        await showSuccess('複製成功', `${label} 已複製到剪貼簿`);
      } catch (err) {
        console.error('複製失敗:', err);
        await showError('複製失敗', '無法複製到剪貼簿');
      }
    };

    // 驗證共用入口 LIFF ID
    const validateCommonLiffId = () => {
      if (!form.commonLiffId.trim()) {
        errors.commonLiffId = '共用入口 LIFF ID 為必填';
        return;
      }
      
      // 檢查格式：必須包含 - 符號
      if (!form.commonLiffId.trim().includes('-')) {
        errors.commonLiffId = 'LIFF ID 格式錯誤，必須包含 - 符號（例如：1234567890-abcdefgh）';
        return;
      }
      
      // 檢查是否與電商模組 LIFF ID 重複
      if (form.ecommerceLiffId.trim() && form.commonLiffId.trim() === form.ecommerceLiffId.trim()) {
        errors.commonLiffId = '共用入口 LIFF ID 不能與電商模組 LIFF ID 相同';
      } else {
        delete errors.commonLiffId;
      }
    };

    // 驗證電商模組 LIFF ID
    const validateEcommerceLiffId = () => {
      if (!form.ecommerceLiffId.trim()) {
        // 電商模組 LIFF ID 為選填，清空錯誤
        delete errors.ecommerceLiffId;
        return;
      }
      
      // 檢查格式：必須包含 - 符號
      if (!form.ecommerceLiffId.trim().includes('-')) {
        errors.ecommerceLiffId = 'LIFF ID 格式錯誤，必須包含 - 符號（例如：1234567890-abcdefgh）';
        return;
      }
      
      // 檢查是否與共用入口 LIFF ID 重複
      if (form.commonLiffId.trim() && form.ecommerceLiffId.trim() === form.commonLiffId.trim()) {
        errors.ecommerceLiffId = '電商模組 LIFF ID 不能與共用入口 LIFF ID 相同';
      } else {
        delete errors.ecommerceLiffId;
      }
    };

    // 驗證 Messaging Channel ID
    const validateMessagingChannelId = () => {
      if (!form.messagingChannelId.trim()) {
        errors.messagingChannelId = 'Messaging Channel ID 為必填';
        return;
      }
      
      if (!/^\d+$/.test(form.messagingChannelId.trim())) {
        errors.messagingChannelId = 'Channel ID 必須是 0-9 的數字';
      } else {
        delete errors.messagingChannelId;
      }
    };

    // 驗證 Messaging Bot Basic ID
    const validateMessagingBotBasicId = () => {
      if (!form.messagingBotBasicId.trim()) {
        errors.messagingBotBasicId = 'Bot Basic ID 為必填';
        return;
      }
      
      if (!/^@[a-zA-Z0-9_-]+$/.test(form.messagingBotBasicId.trim())) {
        errors.messagingBotBasicId = 'Bot Basic ID 必須以 @ 開頭';
      } else {
        delete errors.messagingBotBasicId;
      }
    };

    // 驗證 LINE Login Channel ID
    const validateLoginChannelId = () => {
      if (!form.loginChannelId.trim()) {
        errors.loginChannelId = 'LINE Login Channel ID 為必填';
        return;
      }
      
      if (!/^\d+$/.test(form.loginChannelId.trim())) {
        errors.loginChannelId = 'Channel ID 必須是 0-9 的數字';
      } else {
        delete errors.loginChannelId;
      }
    };

    // 客戶會員模式（用於生成 LIFF Endpoint URL）
    const clientMemberSystemType = ref('LOCKLINE');

    // 取得客戶會員模式
    const fetchClientMemberSystemType = async (clientId) => {
      if (!clientId) {
        // 如果沒有 clientId，嘗試從當前用戶取得
        const user = authService.getCurrentUser();
        if (user && user.clientId) {
          clientId = user.clientId;
        } else {
          return;
        }
      }

      try {
        const response = await clientService.getClient(clientId);
        if (response && response.success && response.data?.clientData) {
          clientMemberSystemType.value = response.data.clientData.memberSystemType || 'LOCKLINE';
        }
      } catch (error) {
        console.error('取得客戶會員模式失敗:', error);
        // 預設為 LOCKLINE
        clientMemberSystemType.value = 'LOCKLINE';
      }
    };

    // 取得前端 LIFF base URL
    const getLiffBaseUrl = () => {
      // 優先使用環境變數
      if (process.env.VUE_APP_LIFF_BASE_URL) {
        return process.env.VUE_APP_LIFF_BASE_URL;
      }
      
      // 開發環境：使用 localhost:8082 (https)
      if (process.env.NODE_ENV === 'development') {
        return 'https://localhost:8082';
      }
      
      // 依 Admin 網域推導（必須先判斷 dev-admin，避免 substring 誤判）
      const origin = window.location.origin;
      if (origin.includes('dev-admin.lockline.io')) {
        return 'https://dev-liff.lockline.io';
      }
      if (origin.includes('admin.lockline.io')) {
        return 'https://liff.lockline.io';
      }
      
      // 預設使用 liff.lockline.io
      return 'https://liff.lockline.io';
    };

    // 生成 LIFF Endpoint URL（共用入口）
    // 規則：根據客戶的會員模式
    // LOCKLINE會員：https://{liff base url}/lockline/lineliff/{line message channel id}
    // WIXTAR會員：https://{liff base url}/wixtar/lineliff/{line message channel id}
    const getLiffEndpointUrl = (liffId) => {
      const baseUrl = getLiffBaseUrl();
      const channelId = form.messagingChannelId || '{Channel ID}';
      const memberMode = clientMemberSystemType.value || 'LOCKLINE';
      
      if (memberMode === 'WIXTAR') {
        return `${baseUrl}/wixtar/lineliff/${channelId}`;
      } else {
        return `${baseUrl}/lockline/lineliff/${channelId}`;
      }
    };

    // 取得客戶編號
    const getClientCode = () => {
      // 優先從已選擇的客戶取得
      if (selectedClient.value && selectedClient.value.clientCode) {
        return selectedClient.value.clientCode;
      }
      
      // 從當前客戶資訊取得
      if (currentClientInfo.value && currentClientInfo.value.clientCode) {
        return currentClientInfo.value.clientCode;
      }
      
      // 從編輯模式的客戶資訊取得
      if (editClientInfo.value && editClientInfo.value.clientCode) {
        return editClientInfo.value.clientCode;
      }
      
      // 從當前用戶取得
      const user = authService.getCurrentUser();
      if (user && user.clientCode) {
        return user.clientCode;
      }
      
      return '';
    };

    // 取得電商模組的 base URL
    const getEcommerceLiffBaseUrl = () => {
      const pub = commerceStorefrontPublicBaseUrl.value.trim();
      if (pub) {
        return pub.replace(/\/$/, '');
      }
      const host = commerceStorefrontHostname.value.trim();
      if (host) {
        return host.startsWith('http://') || host.startsWith('https://')
          ? host.replace(/\/$/, '')
          : `https://${host.replace(/\/$/, '')}`;
      }
      if (form.ecommerceLiffCustomDomain && form.ecommerceLiffCustomDomain.trim()) {
        let customDomain = form.ecommerceLiffCustomDomain.trim();
        if (!customDomain.startsWith('http://') && !customDomain.startsWith('https://')) {
          customDomain = `https://${customDomain}`;
        }
        return customDomain.replace(/\/$/, '');
      }
      if (process.env.VUE_APP_ECOMMERCE_LIFF_BASE_URL) {
        return process.env.VUE_APP_ECOMMERCE_LIFF_BASE_URL;
      }
      return 'https://mall.lockline.io';
    };

    // 使用自動生成的 Callback URL
    const useGeneratedCallbackUrl = () => {
      form.loginCallbackUrl = generatedCallbackUrl.value;
    };

    // 使用自動生成的共用入口 LIFF Endpoint URL
    const useGeneratedCommonLiffEndpointUrl = () => {
      commonLiffEndpointUrl.value = getLiffEndpointUrl(form.commonLiffId);
    };

    // 使用自動生成的電商模組 LIFF Endpoint URL
    const useGeneratedEcommerceLiffEndpointUrl = () => {
      ecommerceLiffEndpointUrl.value = getEcommerceLiffEndpointUrl(form.ecommerceLiffId);
    };

    // 當 Callback URL 欄位獲得焦點時，如果為空則自動填充
    const onCallbackUrlFocus = () => {
      if (!form.loginCallbackUrl && generatedCallbackUrl.value) {
        form.loginCallbackUrl = generatedCallbackUrl.value;
      }
    };

    // 生成電商模組專用 LIFF Endpoint URL
    // 規則：根據客戶的會員模式
    // LOCKLINE會員：https://mall.lockline.io/lineliff/{line message channel id}
    // WIXTAR會員：https://mall.lockline.io/lineliff/{line message channel id}
    const getEcommerceLiffEndpointUrl = (_liffId) => {
      const baseUrl = getEcommerceLiffBaseUrl().replace(/\/$/, '');
      const channelId = form.messagingChannelId || '{Channel ID}';
      return `${baseUrl}/lineliff/${channelId}`;
    };

    // 注意：不自動填充 Callback URL，避免使用者輸入 Channel ID 時鎖定到不完整的值
    // 使用者輸入完 Channel ID 後，可點擊「自動生成」按鈕填入正確的 Callback URL
    // LIFF Endpoint URL：新增時不預填；編輯時僅回填 API 已儲存欄位；其餘由使用者點「自動生成」

    onMounted(() => {
      // 新增模式：只有 system 和 platform 帳號需要選擇客戶，客戶帳號不需要
      if (!isEdit.value) {
        if (needsClientSelection.value) {
          fetchClients();
        } else {
          refreshCommerceStorefrontFields();
          fetchClientMemberSystemType(null);
        }
        // Callback URL 可由聚焦帶入；LIFF Endpoint 不聚焦預填，避免覆寫已儲存或誤改
      } else {
        // 編輯模式：跳過「申請需知」步驟，直接進入「基本設定」
        currentStep.value = 1;
        // 先載入客戶列表（用於顯示客戶資訊），然後載入設定資料
        // 只有 system 和 platform 帳號需要載入客戶列表
        if (needsClientSelection.value) {
          fetchClients().then(() => {
            fetchConfig();
          });
        } else {
          fetchConfig();
        }
      }
    });

    return {
      pageTitle,
      isEdit,
      form,
      configData,
      errors,
      submitting,
      testing,
      loading,
      clients,
      loadingClients,
      needsClientSelection,
      clientSearchKeyword,
      showClientList,
      selectedClient,
      filteredClients,
      handleClientSearch,
      selectClient,
      clearClientSelection,
      currentStep,
      steps,
      canProceedToNextStep,
      canNavigateToStep,
      goToStep,
      nextStep,
      previousStep,
      handleSubmit,
      handleTest,
      handleCancel,
      copyToClipboard,
      formatDateTime,
      expandedSteps,
      toggleStepDetail,
      generatedWebhookUrl,
      generatedCallbackUrl,
      showMessagingChannelSecret,
      showMessagingAccessToken,
      showLoginChannelSecret,
      currentClientInfo,
      getLiffEndpointUrl,
      getEcommerceLiffEndpointUrl,
      ecommerceEdgeHost,
      getClientCode,
      validateCommonLiffId,
      validateEcommerceLiffId,
      validateMessagingChannelId,
      validateMessagingBotBasicId,
      validateLoginChannelId,
      canLinkCommerceSettings,
      useGeneratedCallbackUrl,
      onCallbackUrlFocus,
      commonLiffEndpointUrl,
      ecommerceLiffEndpointUrl,
      useGeneratedCommonLiffEndpointUrl,
      useGeneratedEcommerceLiffEndpointUrl
    };
  }
};
</script>

<style scoped>
.page-outer {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.page-header {
  background: #f5f7fa;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 12px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.page-container {
  padding: 20px;
  padding-top: 80px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.header-actions .btn {
  padding: 6px 12px;
  font-size: 13px;
}

.page-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}

.form-container {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.form-section {
  margin-bottom: 32px;
  padding-bottom: 32px;
  border-bottom: 1px solid #e0e0e0;
}

.form-section:last-child {
  border-bottom: none;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-description {
  color: #666;
  font-size: 14px;
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
}

label.required::after {
  content: ' *';
  color: #dc3545;
}

.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.form-control.is-invalid {
  border-color: #dc3545;
}

.form-hint {
  margin-top: 6px;
  font-size: 12px;
  color: #666;
}

.form-hint-important {
  color: #dc3545 !important;
  font-weight: 600;
  font-size: 13px;
  padding: 8px 12px;
  background: #fee;
  border-left: 3px solid #dc3545;
  border-radius: 4px;
  margin-top: 8px;
}

.invalid-feedback {
  display: block;
  margin-top: 6px;
  font-size: 12px;
  color: #dc3545;
}

.input-group {
  display: flex;
  gap: 8px;
}

.input-group .form-control {
  flex: 1;
}

.liff-id-item {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

.liff-id-item .form-control {
  flex: 1;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #0056b3;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background: #545b62;
}

.btn-info {
  background: #17a2b8;
  color: white;
}

.btn-info:hover:not(:disabled) {
  background: #138496;
}

.btn-danger {
  background: #dc3545;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background: #c82333;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 12px;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* 步驟導航樣式 */
.stepper {
  margin-bottom: 32px;
  position: relative;
  padding-bottom: 40px;
}

.stepper-header {
  display: flex;
  justify-content: space-between;
  position: relative;
  z-index: 2;
}

.step-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;
}

.step-item.disabled {
  opacity: 1;
}

.step-item.disabled .step-number {
  opacity: 1;
  background: #e9ecef;
  border: 3px solid white;
  box-shadow: 0 0 0 2px #e9ecef;
}

.step-item.clickable {
  cursor: pointer;
}

.step-item.clickable:hover .step-number {
  transform: scale(1.1);
  transition: transform 0.2s;
}

.step-item:not(.clickable) {
  cursor: default;
}

.step-number {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #e9ecef;
  color: #6c757d;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 8px;
  transition: all 0.3s;
  position: relative;
  z-index: 3;
  border: 3px solid white;
  box-shadow: 0 0 0 2px #e9ecef;
  opacity: 1;
}

.step-item.active .step-number {
  background: #007bff;
  color: white;
  box-shadow: 0 0 0 2px #007bff;
  opacity: 1;
}

.step-item.completed .step-number {
  background: #28a745;
  color: white;
  box-shadow: 0 0 0 2px #28a745;
  opacity: 1;
}

.step-check {
  display: flex;
  align-items: center;
  justify-content: center;
}

.step-label {
  font-size: 14px;
  font-weight: 500;
  color: #6c757d;
  text-align: center;
  position: relative;
  z-index: 2;
}

.step-item.active .step-label {
  color: #007bff;
  font-weight: 600;
}

.step-item.completed .step-label {
  color: #28a745;
}

.stepper-line {
  height: 2px;
  background: #e9ecef;
  position: absolute;
  top: 20px;
  left: 20px;
  right: 20px;
  z-index: 1;
}

.stepper-progress {
  height: 100%;
  background: #28a745;
  transition: width 0.3s;
}

.step-content {
  min-height: 400px;
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-description {
  color: #666;
  font-size: 14px;
  margin-bottom: 24px;
}

.step-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

/* 資訊展示樣式 */
.info-box {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
}

.info-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #007bff;
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-content {
  color: #333;
  line-height: 1.8;
}

.info-content h4 {
  font-size: 16px;
  font-weight: 600;
  margin-top: 24px;
  margin-bottom: 12px;
  color: #333;
}

.info-content h4:first-child {
  margin-top: 0;
}

.info-content h5 {
  font-size: 14px;
  font-weight: 600;
  margin-top: 20px;
  margin-bottom: 10px;
  color: #555;
}

.info-list {
  margin: 12px 0;
  padding-left: 24px;
}

.info-list li {
  margin-bottom: 8px;
  line-height: 1.6;
}

.info-list a {
  color: #007bff;
  text-decoration: none;
  font-weight: 500;
}

  .info-list a:hover {
    text-decoration: underline;
  }

  .step-item-header {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 8px;
  }

  .step-item-header > span {
    display: inline;
  }

  .btn-detail {
    background: #007bff;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 4px 10px;
    font-size: 12px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    transition: all 0.2s;
    white-space: nowrap;
    margin-left: 4px;
    vertical-align: middle;
  }

  .btn-detail:hover {
    background: #0056b3;
  }

  .step-detail {
    margin-top: 12px;
    margin-left: 24px;
    padding: 16px;
    background: white;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    border-left: 4px solid #007bff;
  }

  .detail-list {
    margin: 0;
    padding-left: 24px;
  }

  .detail-list li {
    margin-bottom: 8px;
    line-height: 1.6;
  }

  .detail-list ul {
    margin-top: 4px;
    margin-bottom: 8px;
    padding-left: 20px;
  }

  .detail-list ul li {
    margin-bottom: 4px;
  }

  .step-links {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #e9ecef;
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }

  .link-external {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #007bff;
    text-decoration: none;
    font-size: 14px;
    padding: 8px 12px;
    background: #f8f9fa;
    border-radius: 4px;
    transition: all 0.2s;
  }

  .link-external:hover {
    background: #e9ecef;
    text-decoration: underline;
  }

  .link-external i {
    font-size: 16px;
  }

  .collapsible-section {
    margin: 20px 0;
  }

  .collapsible-header {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: transparent;
    border: none;
    padding: 12px 0;
    cursor: pointer;
    text-align: left;
    transition: all 0.2s;
  }

  .collapsible-header:hover {
    opacity: 0.8;
  }

  .collapsible-header h4 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #333;
  }

  .collapsible-header i {
    font-size: 20px;
    color: #007bff;
    transition: transform 0.2s;
  }

  .warning-box {
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 6px;
  padding: 16px;
  margin: 20px 0;
}

.warning-box h4 {
  color: #856404;
  margin-top: 0;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.warning-box p {
  color: #856404;
  margin: 0;
}

.danger-alert-box {
  background: #fee;
  border: 2px solid #dc3545;
  border-radius: 6px;
  padding: 16px;
  margin: 20px 0;
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.15);
}

.danger-alert-box h4 {
  color: #dc3545;
  margin-top: 0;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
}

.danger-alert-box h4 i {
  color: #dc3545;
  font-size: 20px;
}

.danger-alert-box p {
  color: #721c24;
  margin: 0;
  font-weight: 500;
}

.explanation-box {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 20px;
  margin: 20px 0;
}

.highlight-text {
  background: #e7f3ff;
  border-left: 4px solid #007bff;
  padding: 12px 16px;
  margin: 16px 0;
  border-radius: 4px;
}

.comparison-table {
  width: 100%;
  border-collapse: collapse;
  margin: 16px 0;
  font-size: 14px;
}

.comparison-table th,
.comparison-table td {
  border: 1px solid #dee2e6;
  padding: 12px;
  text-align: left;
}

.comparison-table th {
  background: #f8f9fa;
  font-weight: 600;
  color: #333;
}

.comparison-table tr:nth-child(even) {
  background: #f8f9fa;
}

.key-points {
  background: #fff3cd;
  border-left: 4px solid #ffc107;
  padding: 12px 16px;
  margin: 16px 0;
  border-radius: 4px;
}

.key-points p {
  margin: 4px 0;
  color: #856404;
}

.note-box {
  background: #d1ecf1;
  border: 1px solid #bee5eb;
  border-radius: 6px;
  padding: 12px 16px;
  margin: 16px 0;
}

.note-box p {
  margin: 0;
  color: #0c5460;
}

.summary-box {
  background: #d4edda;
  border: 1px solid #c3e6cb;
  border-radius: 6px;
  padding: 16px;
  margin: 20px 0;
}

.summary-box p {
  margin: 8px 0;
  color: #155724;
}

.summary-box p:first-child {
  font-weight: 600;
}

/* 客戶搜尋樣式 */
.client-search-wrapper {
  position: relative;
}

.client-search-wrapper .search-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  pointer-events: none;
}

.client-list {
  margin-top: 8px;
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.client-item {
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid #f0f0f0;
}

.client-item:last-child {
  border-bottom: none;
}

.client-item:hover {
  background-color: #f8f9fa;
}

.client-item.selected {
  background-color: #e7f3ff;
  border-left: 3px solid #007bff;
}

.client-info {
  flex: 1;
}

.client-name {
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
}

.client-code {
  font-size: 13px;
  color: #666;
}

.client-item i {
  color: #007bff;
  font-size: 20px;
}

.selected-client {
  margin-top: 12px;
  padding: 12px;
  background: #e7f3ff;
  border: 1px solid #b3d9ff;
  border-radius: 4px;
}

.selected-label {
  font-size: 13px;
  color: #666;
  margin-bottom: 6px;
}

.selected-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.client-code-tag {
  margin-left: 8px;
  padding: 2px 8px;
  background: #007bff;
  color: white;
  border-radius: 3px;
  font-size: 12px;
}

.btn-clear {
  background: none;
  border: none;
  color: #dc3545;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.btn-clear:hover {
  background-color: rgba(220, 53, 69, 0.1);
}

.btn-clear i {
  font-size: 18px;
}

.no-results {
  padding: 24px;
  text-align: center;
  color: #999;
}

.no-results i {
  font-size: 24px;
  margin-bottom: 8px;
  display: block;
}

/* 表單分組樣式 */
.form-group-section {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 20px;
  margin-bottom: 24px;
}

.form-group-section--common-liff {
  background: #eef5ff;
  border-color: #c5d9f2;
}

.form-group-section--common-liff .section-note {
  background: #dbeafe;
  border-left-color: #3b82f6;
  color: #1e3a8a;
}

.form-group-section--common-liff .section-note i {
  color: #2563eb;
}

.form-group-section--ecommerce-liff {
  background: #fff7ed;
  border-color: #f3d5b0;
}

.form-group-section--ecommerce-liff .section-note {
  background: #ffedd5;
  border-left-color: #f59e0b;
  color: #9a3412;
}

.form-group-section--ecommerce-liff .section-note i,
.form-group-section--ecommerce-liff .section-note strong {
  color: #c2410c;
}

.section-note {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: #e7f3ff;
  border-left: 4px solid #007bff;
  border-radius: 4px;
  margin-bottom: 16px;
  color: #004085;
  font-size: 14px;
}

.section-note i {
  color: #007bff;
  font-size: 18px;
  flex-shrink: 0;
}

.section-note strong {
  color: #0056b3;
  font-weight: 600;
}

.field-note {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  padding: 8px 12px;
  background: #e7f3ff;
  border-left: 3px solid #007bff;
  border-radius: 4px;
  color: #004085;
  font-size: 13px;
}

.field-note i {
  color: #007bff;
  font-size: 16px;
  flex-shrink: 0;
}

.field-note strong {
  color: #0056b3;
  font-weight: 600;
}

/* LIFF Endpoint URL 樣式 */
.liff-endpoint-url {
  margin-top: 12px;
  padding: 12px;
  background-color: #fff8f8;
  border-radius: 4px;
  border: 1px solid #f5c2c7;
  border-left: 4px solid #dc3545;
}

/* Endpoint URL 移到 LIFF ID 上方時的間距 */
.liff-endpoint-url--above {
  margin-top: 0;
  margin-bottom: 12px;
}

.liff-endpoint-url .endpoint-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 8px;
}

.liff-endpoint-url .input-group {
  margin-bottom: 8px;
}

.liff-endpoint-url .form-hint {
  font-size: 12px;
  color: #6c757d;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 4px;
}

.liff-endpoint-url .form-hint i {
  font-size: 14px;
}

.liff-endpoint-url .form-hint-endpoint-warning {
  margin-top: 10px;
  margin-bottom: 0;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #b02a37;
  line-height: 1.45;
}

.liff-endpoint-url .liff-endpoint-hint-icon {
  color: #dc3545;
  font-size: 22px;
  flex-shrink: 0;
  line-height: 1.2;
  margin-top: 1px;
  filter: drop-shadow(0 0 1px rgba(220, 53, 69, 0.35));
}

:deep(.form-tip-box .line-storefront-tip-list) {
  margin: 10px 0 0;
  padding-left: 1.35rem;
  font-size: 12px;
  line-height: 1.6;
  color: #4b5563;
}
:deep(.form-tip-box .line-storefront-tip-list li) {
  margin-top: 8px;
}
:deep(.form-tip-box .line-storefront-tip-list li:first-child) {
  margin-top: 0;
}

.cname-copy-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

.cname-copy-value {
  flex: 1;
  min-width: 0;
  padding: 8px 10px;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 13px;
  color: #111827;
  word-break: break-all;
}

.cname-copy-row .btn {
  flex-shrink: 0;
  padding: 6px 12px;
  font-size: 13px;
}

/* 提醒區塊示意圖 */
.alert-guide-image {
  margin-top: 10px;
}

.alert-guide-image img {
  max-width: 100%;
  height: auto;
  border-radius: 6px;
  border: 1px solid #f5c6cb;
}

/* Webhook URL 和 Callback URL 提醒樣式 */
.webhook-url-alert,
.callback-url-alert {
  margin-top: 12px;
  padding: 16px;
  background: #fee;
  border: 2px solid #dc3545;
  border-radius: 6px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.15);
}

.webhook-url-alert .alert-icon,
.callback-url-alert .alert-icon {
  flex-shrink: 0;
  font-size: 24px;
  color: #dc3545;
  margin-top: 2px;
}

.webhook-url-alert .alert-content,
.callback-url-alert .alert-content {
  flex: 1;
}

.webhook-url-alert .alert-title,
.callback-url-alert .alert-title {
  font-size: 14px;
  color: #dc3545;
  margin-bottom: 6px;
  font-weight: 600;
}

.webhook-url-alert .alert-title strong,
.callback-url-alert .alert-title strong {
  font-weight: 700;
  font-size: 15px;
  color: #dc3545;
}

.webhook-url-alert .alert-message,
.callback-url-alert .alert-message {
  font-size: 13px;
  color: #721c24;
  line-height: 1.6;
  font-weight: 500;
}

.webhook-url-alert .alert-message strong,
.callback-url-alert .alert-message strong {
  color: #dc3545;
  font-weight: 600;
}

/* 測試結果區塊（全寬度；勿放在 max-width media 內） */
.test-result-section {
  margin-top: 8px;
}

.test-result-form-section {
  padding-bottom: 0;
  border-bottom: none;
  margin-bottom: 0;
}

.test-result-section-head {
  margin-bottom: 16px;
}

.test-result-section-title {
  margin-bottom: 4px;
  align-items: center;
}

.test-result-section-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%);
  color: #4338ca;
  font-size: 20px;
}

.test-result-section-desc {
  margin: 0 0 0 44px;
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
}

.test-result-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px 22px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 16px rgba(15, 23, 42, 0.04);
}

.test-result-summary {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 14px 20px;
  padding-bottom: 18px;
  margin-bottom: 18px;
  border-bottom: 1px solid #f1f5f9;
}

.test-result-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px 8px 10px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.test-result-pill__icon {
  font-size: 20px;
  line-height: 1;
}

.test-result-pill--success {
  background: #ecfdf5;
  color: #047857;
  border: 1px solid #a7f3d0;
}

.test-result-pill--failed {
  background: #fef2f2;
  color: #b91c1c;
  border: 1px solid #fecaca;
}

.test-result-pill--neutral {
  background: #f8fafc;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.test-result-meta {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px 8px;
  font-size: 13px;
  color: #64748b;
  padding: 8px 12px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #f1f5f9;
}

.test-result-meta i {
  font-size: 16px;
  color: #94a3b8;
  vertical-align: middle;
}

.test-result-meta__label {
  font-weight: 500;
  color: #94a3b8;
}

.test-result-meta__value {
  font-weight: 600;
  color: #334155;
  font-variant-numeric: tabular-nums;
}

.test-result-panel {
  border-radius: 10px;
  padding: 16px 18px;
  margin-top: 0;
}

.test-result-panel--error {
  background: #fffbeb;
  border: 1px solid #fde68a;
}

.test-result-panel--error .test-result-panel__head {
  color: #b45309;
}

.test-result-panel--bot {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.test-result-panel--bot .test-result-panel__head {
  color: #334155;
}

.test-result-panel--hint {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  background: linear-gradient(180deg, #f0f9ff 0%, #f8fafc 100%);
  border: 1px solid #bae6fd;
  padding: 16px 18px;
}

.test-result-panel__head {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 12px;
}

.test-result-panel__head i {
  font-size: 18px;
  opacity: 0.9;
}

.test-result-panel__hint {
  margin: -4px 0 12px;
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
}

.test-result-error-body {
  margin: 0;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid #fcd34d;
  border-radius: 8px;
  font-family: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 12px;
  line-height: 1.55;
  color: #78350f;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 200px;
  overflow-y: auto;
}

.test-result-kv {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.test-result-kv__row {
  display: grid;
  grid-template-columns: minmax(100px, 120px) 1fr;
  gap: 12px 16px;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #e2e8f0;
}

.test-result-kv__row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.test-result-kv__row:first-child {
  padding-top: 0;
}

.test-result-kv__row--avatar {
  align-items: center;
}

.test-result-kv__k {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.test-result-kv__v {
  font-size: 14px;
  color: #0f172a;
  font-weight: 500;
}

.test-result-kv__code {
  font-family: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 12px;
  padding: 6px 10px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  color: #0f172a;
  word-break: break-all;
}

.test-result-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px #e2e8f0, 0 2px 8px rgba(15, 23, 42, 0.08);
}

.test-result-hint__icon {
  flex-shrink: 0;
  font-size: 22px;
  color: #0284c7;
  margin-top: 2px;
}

.test-result-hint__text {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: #475569;
}

.test-result-hint__text strong {
  color: #0f172a;
  font-weight: 600;
}

@media (max-width: 768px) {
  .page-container {
    padding: 12px;
    padding-top: 80px;
  }

  .form-container {
    padding: 16px;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .stepper-header {
    flex-direction: column;
    gap: 16px;
  }

  .step-item {
    width: 100%;
  }

  .stepper-line {
    display: none;
  }

  .step-actions {
    flex-direction: column;
    width: 100%;
  }

  .step-actions .btn {
    width: 100%;
  }

  .comparison-table {
    font-size: 12px;
  }

  .comparison-table th,
  .comparison-table td {
    padding: 8px;
  }

  .info-box {
    padding: 16px;
  }

  .loading-message {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 40px 20px;
    color: #666;
    font-size: 16px;
  }

  .loading-message .spin {
    animation: spin 1s linear infinite;
  }

  .alert {
    padding: 12px 16px;
    border-radius: 4px;
    margin-top: 12px;
  }

  .alert-warning {
    background-color: #fff3cd;
    border: 1px solid #ffeeba;
    color: #856404;
  }

  .alert-warning i {
    margin-right: 8px;
  }

  .test-result-section-desc {
    margin-left: 0;
    margin-top: 8px;
  }

  .test-result-summary {
    flex-direction: column;
    align-items: stretch;
  }

  .test-result-meta {
    width: 100%;
  }

  .test-result-kv__row {
    grid-template-columns: 1fr;
    gap: 4px;
    align-items: start;
  }

  .test-result-kv__row--avatar {
    grid-template-columns: 1fr auto;
  }

}
</style>

<style>
/* LINE 設定頁固定 header（position: fixed，需配合 sidebar 寬度） */
.page-header-line-cfg {
  position: fixed;
  top: 56px;
  left: 260px;
  right: 0;
  z-index: 90;
}

/* sidebar 收合時調整 left */
.sidebar.collapsed ~ .main-content .page-header-line-cfg {
  left: 64px;
}

@media (max-width: 768px) {
  .page-header-line-cfg {
    left: 0 !important;
    top: 56px;
  }
}
</style>
